import React from "react"
import PropTypes from "prop-types"
import { makeStyles } from "@material-ui/core/styles"
import { TextField, InputAdornment, Box } from "@material-ui/core"
import SearchIcon from "@material-ui/icons/Search"

const useStyles = makeStyles((theme) => ({
  searchContainer: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  searchField: {
    backgroundColor: "#ffffff",
    borderRadius: theme.shape.borderRadius,
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.light,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: theme.spacing(1.5, 2),
    },
  },
  searchIcon: {
    color: theme.palette.text.secondary,
  },
}))

/**
 * CustomSearch component for consistent search inputs across the application
 *
 * @param {Object} props - Component props
 * @param {string} props.value - Search input value
 * @param {function} props.onChange - Function to handle changes
 * @param {string} [props.placeholder='Search...'] - Placeholder text
 * @param {Object} [props.containerStyle] - Additional styles for the container
 * @param {Object} [props.inputStyle] - Additional styles for the input field
 * @param {string} [props.className] - Additional CSS class name
 * @param {boolean} [props.fullWidth=true] - Whether the input should take full width
 * @param {string} [props.size='small'] - Input size ('small' or 'medium')
 * @param {Object} [props.rest] - Additional props to pass to TextField
 */
const CustomSearch = ({
  value,
  onChange,
  placeholder = "Search...",
  containerStyle = {},
  inputStyle = {},
  className = "",
  fullWidth = true,
  size = "small",
  ...rest
}) => {
  // Ensure size is either "small" or "medium"
  const textFieldSize = size === "medium" ? "medium" : "small";
  const classes = useStyles()

  // Handle change to pass the value directly
  const handleChange = (e) => {
    onChange(e.target.value)
  }

  return (
    <Box className={`${classes.searchContainer} ${className}`} style={containerStyle}>
      <TextField
        variant="outlined"
        placeholder={placeholder}
        value={value}
        size={textFieldSize}
        fullWidth={fullWidth}
        className={classes.searchField}
        onChange={handleChange}
        style={inputStyle}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon className={classes.searchIcon} />
            </InputAdornment>
          ),
        }}
        {...rest}
      />
    </Box>
  )
}

CustomSearch.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  containerStyle: PropTypes.object,
  inputStyle: PropTypes.object,
  className: PropTypes.string,
  fullWidth: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium"]),
}

export default CustomSearch